import React, { useState } from 'react';
import { Mail, Loader2, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  'YouTube',
  'Instagram',
  'TikTok',
  'Podcast',
  'Newsletter',
  'X / Twitter',
  'Facebook',
  'LinkedIn',
  'Coaching / community',
  'Other',
];

const AUDIENCE_SIZES = [
  'Under 1,000',
  '1k – 10k',
  '10k – 50k',
  '50k – 250k',
  '250k – 1M',
  '1M+',
];

/**
 * AffiliateApplyForm
 *
 * Inline, self-contained form for the /affiliate page. Handles its own
 * validation state, submission to POST /api/affiliate/apply, honeypot,
 * and renders a "check your email" success state once the server accepts
 * the application.
 */
const AffiliateApplyForm = () => {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    platform: 'YouTube',
    audience_size: '1k – 10k',
    description: '',
    website_url: '',
    agreed_to_terms: false,
    hp_referrer: '', // honeypot — must stay empty
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!form.agreed_to_terms) {
      setErrorMsg('Please agree to the affiliate terms to continue.');
      return;
    }
    if (form.description.trim().length < 10) {
      setErrorMsg('Tell us a bit more about why Levanta fits (at least a sentence).');
      return;
    }
    setStatus('submitting');
    try {
      const res = await fetch(`${BACKEND_URL}/api/affiliate/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Something went wrong. Please try again.');
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  if (status === 'success') {
    return (
      <div
        data-testid="affiliate-apply-success"
        className="rounded-3xl bg-gradient-to-br from-white/10 to-white/5 ring-1 ring-white/15 backdrop-blur-md p-10 md:p-14 text-center"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/40 flex items-center justify-center mb-6">
          <Mail className="w-7 h-7 text-emerald-300" strokeWidth={2} />
        </div>
        <h3 className="text-3xl md:text-4xl font-black tracking-tight mb-4">
          Check your inbox.
        </h3>
        <p className="text-white/75 max-w-xl mx-auto text-[17px] leading-relaxed">
          We just sent a verification link to{' '}
          <strong className="text-white">{form.email}</strong>. Click it to
          activate your partnership and receive your unique 8-character
          referral code.
        </p>
        <p className="text-white/50 text-sm mt-5">
          The link expires in 48 hours. Don't see it? Check spam, then write to
          business@levanta.life.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="affiliate-apply-form"
      className="rounded-3xl bg-gradient-to-br from-white/10 to-white/5 ring-1 ring-white/15 backdrop-blur-md p-7 md:p-10"
      noValidate
    >
      <div className="grid md:grid-cols-2 gap-5">
        <label className="block">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300 mb-2 block">
            Full name
          </span>
          <input
            type="text"
            required
            minLength={2}
            maxLength={120}
            autoComplete="name"
            value={form.full_name}
            onChange={(e) => update('full_name', e.target.value)}
            data-testid="affiliate-input-name"
            className="w-full bg-white/5 text-white placeholder-white/40 px-5 py-3.5 rounded-2xl ring-1 ring-white/15 focus:ring-cyan-300 focus:bg-white/10 outline-none transition"
            placeholder="Jane Doe"
          />
        </label>
        <label className="block">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300 mb-2 block">
            Email
          </span>
          <input
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            data-testid="affiliate-input-email"
            className="w-full bg-white/5 text-white placeholder-white/40 px-5 py-3.5 rounded-2xl ring-1 ring-white/15 focus:ring-cyan-300 focus:bg-white/10 outline-none transition"
            placeholder="jane@example.com"
          />
        </label>
        <label className="block">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300 mb-2 block">
            Main platform
          </span>
          <select
            required
            value={form.platform}
            onChange={(e) => update('platform', e.target.value)}
            data-testid="affiliate-input-platform"
            className="w-full bg-white/5 text-white px-5 py-3.5 rounded-2xl ring-1 ring-white/15 focus:ring-cyan-300 focus:bg-white/10 outline-none transition appearance-none"
          >
            {PLATFORMS.map((p) => (
              <option key={p} value={p} className="text-[#0b1f3a]">
                {p}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300 mb-2 block">
            Audience size (approx.)
          </span>
          <select
            required
            value={form.audience_size}
            onChange={(e) => update('audience_size', e.target.value)}
            data-testid="affiliate-input-audience"
            className="w-full bg-white/5 text-white px-5 py-3.5 rounded-2xl ring-1 ring-white/15 focus:ring-cyan-300 focus:bg-white/10 outline-none transition appearance-none"
          >
            {AUDIENCE_SIZES.map((a) => (
              <option key={a} value={a} className="text-[#0b1f3a]">
                {a}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block mt-5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300 mb-2 block">
          Your website / channel <span className="text-white/55 font-normal normal-case tracking-normal">(optional)</span>
        </span>
        <input
          type="url"
          maxLength={400}
          value={form.website_url}
          onChange={(e) => update('website_url', e.target.value)}
          data-testid="affiliate-input-website"
          className="w-full bg-white/5 text-white placeholder-white/40 px-5 py-3.5 rounded-2xl ring-1 ring-white/15 focus:ring-cyan-300 focus:bg-white/10 outline-none transition"
          placeholder="https://your-channel.com"
        />
      </label>

      <label className="block mt-5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-300 mb-2 block">
          Why Levanta fits your audience
        </span>
        <textarea
          required
          minLength={10}
          maxLength={1200}
          rows={4}
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          data-testid="affiliate-input-description"
          className="w-full bg-white/5 text-white placeholder-white/40 px-5 py-3.5 rounded-2xl ring-1 ring-white/15 focus:ring-cyan-300 focus:bg-white/10 outline-none transition resize-none"
          placeholder="Tell us a sentence or two about your audience and why a structured growth system would resonate with them."
        />
      </label>

      {/* Honeypot — invisible to humans, catches bots. */}
      <div aria-hidden="true" className="absolute -left-[9999px] top-0 w-px h-px overflow-hidden">
        <label>
          Referrer
          <input
            type="text"
            tabIndex="-1"
            autoComplete="off"
            value={form.hp_referrer}
            onChange={(e) => update('hp_referrer', e.target.value)}
          />
        </label>
      </div>

      <label className="mt-6 flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={form.agreed_to_terms}
          onChange={(e) => update('agreed_to_terms', e.target.checked)}
          data-testid="affiliate-input-terms"
          className="mt-1 w-5 h-5 rounded border-white/30 bg-white/10 text-cyan-400 focus:ring-cyan-300 accent-cyan-400 cursor-pointer"
        />
        <span className="text-sm text-white/75 leading-relaxed">
          I agree to the{' '}
          <a
            href="/affiliate/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-white"
          >
            Levanta Affiliate Program terms
          </a>
          , will disclose the partnership in all promotion (#ad / #partner /
          #affiliate), and will represent Levanta accurately.
        </span>
      </label>

      {errorMsg && (
        <div
          data-testid="affiliate-apply-error"
          className="mt-5 flex items-start gap-3 bg-rose-500/10 ring-1 ring-rose-400/40 text-rose-200 rounded-2xl px-5 py-4 text-sm"
        >
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{errorMsg}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        data-testid="affiliate-apply-submit"
        className="mt-7 w-full inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-white text-[#0b1f3a] font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:-translate-y-0"
      >
        {status === 'submitting' ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending verification email…
          </>
        ) : (
          <>
            Apply &amp; get my code
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>

      <p className="mt-5 text-center text-white/50 text-sm flex items-center justify-center gap-2">
        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
        Reviewed + approved automatically on email verification.
      </p>
    </form>
  );
};

export default AffiliateApplyForm;
